import { Suspense, lazy } from 'react';
import type { RichTextEditorProps } from './RichTextEditor';

/**
 * Lazily loaded so Tiptap and ProseMirror stay out of the initial bundle.
 *
 * The editor pulls `@tiptap/*` plus the whole `prosemirror-*` family — ~800 KB of source,
 * the largest dependency in the app after apexcharts. Two screens use it (the agreement
 * template form and the notification content editor), both behind lazy routes, so importing
 * it at module scope put it in front of every sign-in for nothing.
 *
 * ### Why the indirection is a separate file
 *
 * `@realtyfeed/ui`'s barrel is imported eagerly by the shell, and a barrel re-export keeps a
 * module in the entry's static graph. Our own component body is tree-shaken out of it, but
 * the `node_modules` packages it imports are not: without a `sideEffects: false` of their own
 * they are assumed to have side effects and get hoisted into the entry chunk anyway — which
 * is exactly what the 5H.2 bundle analysis found. Splitting the dynamic `import()` into its
 * own module is what actually severs the static edge; see `ApexChart` for the same pattern
 * around apexcharts (docs/07-decisions.md D20).
 *
 * The props type is re-exported from the implementation as a `type`, which is erased at
 * build time and so does not re-create the edge.
 */
const Editor = lazy(() => import('./RichTextEditor').then((m) => ({ default: m.RichTextEditor })));

/**
 * The editor's outer shell, drawn while the chunk is in flight.
 *
 * Sized to the real thing — label, rounded border, toolbar strip, body — so the form does
 * not jump when the editor arrives. A bare spinner here would collapse the field to nothing
 * and shift every control below it.
 */
function EditorSkeleton({ label, id }: Pick<RichTextEditorProps, 'label' | 'id'>) {
  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-slate-800 dark:text-slate-200">
          {label}
        </label>
      )}
      <div
        // Announced rather than silent: a form field that is not yet interactive should say
        // so, not read as an empty box.
        role="status"
        aria-label="Loading editor"
        className="w-full rounded-[16px] border-[0.5px] border-ds-border-default bg-ds-bg-page p-4"
      >
        <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-ds-border-default pb-3">
          {Array.from({ length: 8 }, (_, i) => (
            <span
              key={i}
              className="h-8 w-8 animate-pulse rounded bg-slate-200 dark:bg-slate-700"
              aria-hidden
            />
          ))}
        </div>
        <div className="h-[150px] animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
}

/**
 * Controlled rich-text editor. See `./RichTextEditor` for the editor itself and what its
 * toolbar covers — this file only defers loading it.
 */
export function RichTextEditor(props: RichTextEditorProps) {
  return (
    <Suspense fallback={<EditorSkeleton label={props.label} id={props.id} />}>
      <Editor {...props} />
    </Suspense>
  );
}
